import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import DataTable from "../../common/ui/data-table/DataTable";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import "../documents/Documents.css";
import "./TimeSheets.css";
import "./TimeLogPage.css";
import { getTimeSheetStatusLabel, TimeSheetStatus } from "./timesheetConstants";
import {
  addDays,
  addMonths,
  formatMonthRangeLabel,
  formatMonthYearLabel,
  formatWeekRangeReadable,
  getTimesheetNavWeekStartDate,
  getTimesheetNavWeekStartKey,
  getWeekStartKey,
  getWeekStartMonday,
  getWeekStartsOverlappingMonth,
  startOfMonth,
  toUtcDateOnlyISO,
} from "./timesheetDateUtils";
import {
  fetchWeeklyTimeSheet,
  fetchTimeLog,
  fetchTimesheetDetail,
  fetchTimeLogExportBlob,
  triggerBlobDownload,
  fetchTimesheetProjects,
  mapTimesheetProjectsResponse,
  handleTimesheetApiError,
  unsubmitTimeSheet,
  submitTimeSheetForApproval,
  TIME_LOG_VIEW_MODE,
} from "./timesheetApi";
import TimeSheetStatusBadge from "./TimeSheetStatusBadge";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";
import {
  normalizeTimesheetUserId,
  subscribeTimesheetRealtime,
  timesheetWeekStartsInVisibleMonth,
} from "./timesheetRealtimeBus";

const VIEW = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
};

const PAGE_SIZE = 20;

const canEditTimesheet = (status) =>
  status !== TimeSheetStatus.Approved && status !== TimeSheetStatus.Rejected;

const showRevertForStatus = (status) => status === TimeSheetStatus.Submitted;

/**
 * Submit from the grid (not from entry form). Returned timesheets use the same "Submit" label; tooltip explains resubmission.
 * Rejected / Approved: show Submit for layout consistency, disabled.
 * @returns {"none" | "submit" | "resubmit" | "disabled_rejected" | "disabled_approved"}
 */
const getTimeLogSubmitCta = (status, timeSheetId) => {
  if (!timeSheetId) return "none";
  if (status === TimeSheetStatus.Approved) return "disabled_approved";
  if (status === TimeSheetStatus.Rejected) return "disabled_rejected";
  if (status === TimeSheetStatus.Draft) return "submit";
  if (status === TimeSheetStatus.Returned) return "resubmit";
  return "none";
};

const submitCtaShowsDisabledSubmit = (cta) => cta === "disabled_rejected" || cta === "disabled_approved";

const monthWeekStartKey = (d) => {
  if (!d) return null;
  try {
    return getWeekStartMonday(d).getTime();
  } catch {
    return null;
  }
};

const rowHasTimeSheetId = (row) => String(row?.timeSheetId ?? "").trim().length > 0;

/** Newest week first (later Monday on top; earliest week of the month at the bottom). */
const sortMonthlyRowsNewestFirst = (rows) =>
  [...rows].sort((a, b) => {
    const ka = monthWeekStartKey(a.weekStart);
    const kb = monthWeekStartKey(b.weekStart);
    if (ka == null && kb == null) return 0;
    if (ka == null) return 1;
    if (kb == null) return -1;
    return kb - ka;
  });

/** @returns {"unsubmitted" | "submitted" | "approved" | "rejected" | "returned"} */
const bucketMonthlyStatus = (row) => {
  if (row?.isMissingWeekPlaceholder) return "unsubmitted";
  if (!rowHasTimeSheetId(row)) return "unsubmitted";
  const s = row.status;
  if (s === TimeSheetStatus.Draft) return "unsubmitted";
  if (s === TimeSheetStatus.Submitted) return "submitted";
  if (s === TimeSheetStatus.Approved) return "approved";
  if (s === TimeSheetStatus.Rejected) return "rejected";
  if (s === TimeSheetStatus.Returned) return "returned";
  return "unsubmitted";
};

/** @returns {"unsubmitted" | "submitted" | "approved" | "rejected" | "returned"} */
const bucketWeeklyMeta = (meta) => {
  if (!meta?.timeSheetId || String(meta.timeSheetId).trim() === "") return "unsubmitted";
  const s = meta.status;
  if (s === TimeSheetStatus.Draft) return "unsubmitted";
  if (s === TimeSheetStatus.Submitted) return "submitted";
  if (s === TimeSheetStatus.Approved) return "approved";
  if (s === TimeSheetStatus.Rejected) return "rejected";
  if (s === TimeSheetStatus.Returned) return "returned";
  return "unsubmitted";
};

const TL_STATS_ORDER = ["unsubmitted", "submitted", "approved", "returned", "rejected"];

const TL_STATS_LABEL_KEY = {
  unsubmitted: "statsUnsubmitted",
  submitted: "statsSubmitted",
  approved: "statsApproved",
  returned: "statsReturned",
  rejected: "statsRejected",
};

const TL_STATS_ICON = {
  unsubmitted: "bi bi-calendar-plus",
  submitted: "bi bi-hourglass-split",
  approved: "bi bi-check-circle-fill",
  returned: "bi bi-arrow-return-left",
  rejected: "bi bi-x-circle-fill",
};

const TimeLogPage = () => {
  const { t } = useTranslation();
  const timeLogSubmitButtonTitle = (cta) => {
    if (cta === "disabled_rejected") return t("app.timesheets.timeLog.submitDisabledRejected");
    if (cta === "disabled_approved") return t("app.timesheets.timeLog.submitDisabledApproved");
    if (cta === "resubmit") return t("app.timesheets.timeLog.submitReturnedTooltip");
    if (cta === "submit") return t("app.timesheets.timeLog.submitForApprovalTooltip");
    return t("app.timesheets.timeLog.submitForApprovalTooltip");
  };
  const history = useHistory();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("timesheets");
  const [viewMode, setViewMode] = useState(VIEW.MONTHLY);
  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const [focusWeekStart, setFocusWeekStart] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [detailDto, setDetailDto] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState({
    project: "all",
    status: "all",
  });
  const [entryNavLoading, setEntryNavLoading] = useState(false);
  const [timeLogPaged, setTimeLogPaged] = useState(null);
  const [timeLogLoading, setTimeLogLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [projectOptions, setProjectOptions] = useState([]);
  const [exportLoading, setExportLoading] = useState(null);
  const [revertLoading, setRevertLoading] = useState(false);
  const [submitApprovalLoading, setSubmitApprovalLoading] = useState(false);
  const [timeLogRefreshKey, setTimeLogRefreshKey] = useState(0);

  const timeLogRealtimeRef = useRef({});
  timeLogRealtimeRef.current = {
    viewMode,
    monthStart,
    focusWeekStart,
    detailRow,
    setTimeLogRefreshKey,
    setDetailRow,
    setDetailDto,
    setDetailLoading,
  };

  useEffect(() => {
    return subscribeTimesheetRealtime((data) => {
      const auth = WebStorage.getLocalStore(WebStorageNames.AuthInfo);
      const selfId = normalizeTimesheetUserId(auth?.selfUserId);
      const empId = normalizeTimesheetUserId(data?.employeeUserId);
      if (!empId || !selfId || empId !== selfId) return;

      const {
        viewMode: vm,
        monthStart: ms,
        focusWeekStart: fws,
        detailRow: dr,
        setTimeLogRefreshKey: bump,
        setDetailRow: setRow,
        setDetailDto,
        setDetailLoading,
      } = timeLogRealtimeRef.current;

      let scopeOk = false;
      if (vm === VIEW.MONTHLY) {
        scopeOk = timesheetWeekStartsInVisibleMonth(data.weekStartDate, ms);
      } else {
        const ws = fws || getWeekStartMonday(ms);
        scopeOk = getWeekStartKey(ws) === data.weekStartDate;
      }
      if (!scopeOk) return;

      bump((k) => k + 1);

      if (dr?.timeSheetId != null && String(dr.timeSheetId) === String(data.timeSheetId)) {
        setDetailLoading(true);
        fetchTimesheetDetail({ timeSheetId: dr.timeSheetId })
          .then((dto) => setDetailDto(dto))
          .catch((e) => {
            setRow(null);
            handleTimesheetApiError(e, t("app.timesheets.timeLog.detailLoadFailed"));
          })
          .finally(() => setDetailLoading(false));
      }
    });
  }, [t]);

  const weekStartsInMonth = useMemo(() => getWeekStartsOverlappingMonth(monthStart), [monthStart]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loadOnce = async () => {
        const raw = await fetchTimesheetProjects();
        if (cancelled) return [];
        return mapTimesheetProjectsResponse(raw);
      };
      try {
        let list = await loadOnce();
        /* Second attempt after user session is fully available or slower API envelope */
        if (!cancelled && list.length === 0) {
          await new Promise((r) => setTimeout(r, 400));
          if (!cancelled) list = await loadOnce();
        }
        if (!cancelled) setProjectOptions(list);
      } catch {
        if (!cancelled) setProjectOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [monthStart, viewMode, filters.status, filters.project]);

  useEffect(() => {
    if (viewMode !== VIEW.WEEKLY) return;
    setPage(1);
  }, [focusWeekStart, viewMode]);

  useEffect(() => {
    if (!weekStartsInMonth.length) {
      setFocusWeekStart(null);
      return;
    }
    setFocusWeekStart((prev) => {
      if (!prev) return weekStartsInMonth[0];
      const ok = weekStartsInMonth.some((w) => w.getTime() === prev.getTime());
      return ok ? prev : weekStartsInMonth[0];
    });
  }, [monthStart, weekStartsInMonth]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTimeLogLoading(true);
      try {
        const base = {
          page,
          pageSize: PAGE_SIZE,
        };
        if (filters.status !== "all") base.status = Number(filters.status);
        if (filters.project !== "all") base.projectId = filters.project;

        if (viewMode === VIEW.MONTHLY) {
          const data = await fetchTimeLog({
            ...base,
            viewMode: TIME_LOG_VIEW_MODE.Monthly,
            year: monthStart.getFullYear(),
            month: monthStart.getMonth() + 1,
          });
          if (!cancelled) setTimeLogPaged(data);
        } else {
          const ws = focusWeekStart || getWeekStartMonday(monthStart);
          const we = addDays(ws, 6);
          const data = await fetchTimeLog({
            ...base,
            viewMode: TIME_LOG_VIEW_MODE.Weekly,
            weekStartDate: toUtcDateOnlyISO(ws),
            weekEndDate: toUtcDateOnlyISO(we),
          });
          if (!cancelled) setTimeLogPaged(data);
        }
      } catch (e) {
        if (!cancelled) {
          setTimeLogPaged(null);
          handleTimesheetApiError(e, t("app.timesheets.timeLog.loadFailed"));
        }
      } finally {
        if (!cancelled) setTimeLogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, monthStart, focusWeekStart, page, filters.status, filters.project, timeLogRefreshKey, t]);

  const monthlyRows = timeLogPaged?.monthlyRows ?? [];
  const weeklyRows = timeLogPaged?.weeklyRows ?? [];
  const totalCount = timeLogPaged?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);

  const focusWeekMeta = useMemo(() => {
    const ws = focusWeekStart || getWeekStartMonday(monthStart);
    const we = addDays(ws, 6);
    const wh = weeklyRows.reduce((a, d) => a + d.dailyHours, 0);
    const st = weeklyRows[0]?.status ?? 0;
    const tid = weeklyRows[0]?.timeSheetId ?? "";
    return { weekStart: ws, weekEnd: we, weeklyHours: wh, status: st, timeSheetId: tid };
  }, [focusWeekStart, monthStart, weeklyRows]);

  const monthlyTotalHours = useMemo(() => monthlyRows.reduce((a, w) => a + (w.weeklyHours || 0), 0), [monthlyRows]);

  /** Every week in the month has a row and each is Submitted or Approved — no “add next week” prompt. */
  const wholeMonthTimesheetSubmitted = useMemo(() => {
    const weekCount = weekStartsInMonth.length;
    if (weekCount === 0 || totalCount < weekCount) return false;
    if (totalPages > 1) return false;
    if (monthlyRows.length !== totalCount) return false;
    return monthlyRows.every(
      (r) => r.status === TimeSheetStatus.Submitted || r.status === TimeSheetStatus.Approved
    );
  }, [weekStartsInMonth, totalCount, totalPages, monthlyRows]);

  /** Next calendar week (Mon) onward: first week in the visible month with no API row — prompt to add hours. Only while viewing the current month. */
  const missingNextWeekStart = useMemo(() => {
    if (viewMode !== VIEW.MONTHLY) return null;
    if (timeLogLoading) return null;

    const now = new Date();
    const inCurrentMonth =
      monthStart.getFullYear() === now.getFullYear() && monthStart.getMonth() === now.getMonth();
    if (!inCurrentMonth) return null;

    if (wholeMonthTimesheetSubmitted) return null;

    if (filters.status !== "all" || filters.project !== "all") return null;
    if (totalPages > 1 && page !== totalPages) return null;

    const nextWeekMonday = addDays(getWeekStartMonday(new Date()), 7);
    const rowWeekKeys = new Set(
      monthlyRows
        .map((r) => (r.weekStart ? getWeekStartMonday(r.weekStart).getTime() : null))
        .filter((t) => t != null)
    );

    for (const ws of weekStartsInMonth) {
      const wk = getWeekStartMonday(ws);
      if (wk.getTime() < nextWeekMonday.getTime()) continue;
      if (!rowWeekKeys.has(wk.getTime())) return wk;
    }
    return null;
  }, [
    viewMode,
    timeLogLoading,
    monthStart,
    filters.status,
    filters.project,
    totalPages,
    page,
    monthlyRows,
    weekStartsInMonth,
    wholeMonthTimesheetSubmitted,
  ]);

  /**
   * Monthly grid rows: when the API returns the full month (single page, no filters), merge in every
   * calendar week in the month — insert placeholder rows for weeks with no API row. Otherwise keep
   * API order and optionally prepend the “next missing week” prompt. Rows without timeSheetId use the
   * same placeholder-style UI as synthetic gaps.
   */
  const monthlyTableRows = useMemo(() => {
    const makeGapPlaceholder = (wk) => {
      const wkNorm = getWeekStartMonday(wk);
      const we = addDays(wkNorm, 6);
      return {
        id: `__missing_week__${wkNorm.getTime()}`,
        isMissingWeekPlaceholder: true,
        weekStart: wkNorm,
        weekEnd: we,
        weekLabel: formatWeekRangeReadable(wkNorm, we),
      };
    };

    if (timeLogLoading) {
      return viewMode === VIEW.MONTHLY ? sortMonthlyRowsNewestFirst(monthlyRows) : monthlyRows;
    }

    const mergeFullMonth =
      viewMode === VIEW.MONTHLY &&
      totalPages === 1 &&
      filters.status === "all" &&
      filters.project === "all" &&
      weekStartsInMonth.length > 0;

    if (mergeFullMonth) {
      const byWeek = new Map();
      for (const r of monthlyRows) {
        const k = monthWeekStartKey(r.weekStart);
        if (k != null) byWeek.set(k, r);
      }
      return sortMonthlyRowsNewestFirst(
        weekStartsInMonth.map((ws) => {
          const wk = getWeekStartMonday(ws);
          const k = wk.getTime();
          const existing = byWeek.get(k);
          if (existing) return existing;
          return makeGapPlaceholder(wk);
        })
      );
    }

    let rows = [...monthlyRows];
    if (missingNextWeekStart) {
      const k = monthWeekStartKey(missingNextWeekStart);
      const hasWeek = rows.some((r) => monthWeekStartKey(r.weekStart) === k);
      if (k != null && !hasWeek) {
        rows = [makeGapPlaceholder(missingNextWeekStart), ...rows];
      }
    }
    return sortMonthlyRowsNewestFirst(rows);
  }, [
    monthlyRows,
    missingNextWeekStart,
    timeLogLoading,
    viewMode,
    totalPages,
    filters.status,
    filters.project,
    weekStartsInMonth,
  ]);

  const rangeLabel = formatMonthRangeLabel(monthStart);

  const timeLogScopeStats = useMemo(() => {
    const counts = {
      unsubmitted: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
      returned: 0,
    };
    let total = 0;
    if (viewMode === VIEW.MONTHLY) {
      for (const row of monthlyTableRows) {
        const b = bucketMonthlyStatus(row);
        counts[b] += 1;
        total += 1;
      }
    } else {
      const b = bucketWeeklyMeta(focusWeekMeta);
      counts[b] = 1;
      total = 1;
    }
    /* Submitted for the approval workflow (pending, approved, or rejected) — excludes draft / missing / returned. */
    const submissionProgressPct =
      total > 0
        ? Math.round(
            ((counts.submitted + counts.approved + counts.rejected) / total) * 100
          )
        : 0;
    return { counts, total, submissionProgressPct };
  }, [viewMode, monthlyTableRows, focusWeekMeta]);

  const timeLogStatsScopeLabel =
    viewMode === VIEW.MONTHLY
      ? rangeLabel
      : formatWeekRangeReadable(focusWeekMeta.weekStart, focusWeekMeta.weekEnd);

  /** Weekly: compact month in toolbar; full range kept for title/tooltip. */
  const periodPillLabel = viewMode === VIEW.WEEKLY ? formatMonthYearLabel(monthStart) : rangeLabel;

  const buildTimeLogQuery = useCallback(() => {
    const q = {};
    if (filters.status !== "all") q.status = Number(filters.status);
    if (filters.project !== "all") q.projectId = filters.project;
    if (viewMode === VIEW.MONTHLY) {
      q.viewMode = TIME_LOG_VIEW_MODE.Monthly;
      q.year = monthStart.getFullYear();
      q.month = monthStart.getMonth() + 1;
    } else {
      q.viewMode = TIME_LOG_VIEW_MODE.Weekly;
      const ws = focusWeekStart || getWeekStartMonday(monthStart);
      const we = addDays(ws, 6);
      q.weekStartDate = toUtcDateOnlyISO(ws);
      q.weekEndDate = toUtcDateOnlyISO(we);
    }
    return q;
  }, [viewMode, monthStart, focusWeekStart, filters.status, filters.project]);

  const runExport = async (format) => {
    setExportLoading(format);
    try {
      const q = buildTimeLogQuery();
      const blob = await fetchTimeLogExportBlob(format, q);
      const ext = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
      const stamp = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(blob, `time-log-${stamp}.${ext}`);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.timeLog.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const shiftMonth = (delta) => {
    setMonthStart((m) => startOfMonth(addMonths(m, delta)));
  };

  const showJumpToCurrentPeriod = useMemo(() => {
    const now = new Date();
    const inCurrentMonth =
      monthStart.getFullYear() === now.getFullYear() && monthStart.getMonth() === now.getMonth();
    if (viewMode === VIEW.MONTHLY) return !inCurrentMonth;
    const tw = getWeekStartMonday(now);
    const onCurrentWeek = focusWeekStart != null && focusWeekStart.getTime() === tw.getTime();
    return !inCurrentMonth || !onCurrentWeek;
  }, [monthStart, viewMode, focusWeekStart]);

  const jumpToCurrentPeriod = () => {
    const now = new Date();
    setMonthStart(startOfMonth(now));
    if (viewMode === VIEW.WEEKLY) {
      setFocusWeekStart(getWeekStartMonday(now));
    }
  };

  const shiftFocusWeek = (delta) => {
    const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
    if (i < 0) return;
    const next = weekStartsInMonth[i + delta];
    if (next) setFocusWeekStart(next);
  };

  const openMonthlyDetail = async (row) => {
    if (!row.timeSheetId) {
      Common.showErrorMessage(t("app.timesheets.timeLog.missingTimeSheetId"));
      return;
    }
    setDetailRow(row);
    setDetailDto(null);
    setDetailLoading(true);
    try {
      const dto = await fetchTimesheetDetail({ timeSheetId: row.timeSheetId });
      setDetailDto(dto);
    } catch (e) {
      setDetailRow(null);
      handleTimesheetApiError(e, t("app.timesheets.timeLog.detailLoadFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  const openWeeklyDetail = async () => {
    const tid = focusWeekMeta.timeSheetId;
    if (!tid) {
      Common.showErrorMessage(t("app.timesheets.timeLog.missingTimeSheetId"));
      return;
    }
    setDetailRow({
      timeSheetId: tid,
      weekStart: focusWeekMeta.weekStart,
      weekEnd: focusWeekMeta.weekEnd,
      status: focusWeekMeta.status,
    });
    setDetailDto(null);
    setDetailLoading(true);
    try {
      const dto = await fetchTimesheetDetail({ timeSheetId: tid });
      setDetailDto(dto);
    } catch (e) {
      setDetailRow(null);
      handleTimesheetApiError(e, t("app.timesheets.timeLog.detailLoadFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  const navigateToWeeklyEntry = async (weekStartDate) => {
    const ws = getTimesheetNavWeekStartDate(weekStartDate);
    if (!ws) {
      Common.showErrorMessage(t("app.timesheets.timeLog.missingWeekForEdit"));
      return;
    }
    setEntryNavLoading(true);
    try {
      await fetchWeeklyTimeSheet(ws);
      history.push("/timesheets/time-log/new", { weekStartKey: getTimesheetNavWeekStartKey(weekStartDate) });
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.entry.weeklyLoadFailed"));
    } finally {
      setEntryNavLoading(false);
    }
  };

  const goToNewEntry = () => {
    withWorkplace(() => navigateToWeeklyEntry(focusWeekMeta.weekStart || weekStartsInMonth[0] || monthStart));
  };

  const goToEditEntry = (row) => {
    withWorkplace(() => {
      const wsRaw = row.weekStart || focusWeekMeta.weekStart;
      if (!wsRaw) {
        Common.showErrorMessage(t("app.timesheets.timeLog.missingWeekForEdit"));
        return;
      }
      if (!row.timeSheetId) {
        Common.showErrorMessage(t("app.timesheets.timeLog.missingTimeSheetId"));
        return;
      }
      const weekKey = getTimesheetNavWeekStartKey(wsRaw);
      if (!weekKey) {
        Common.showErrorMessage(t("app.timesheets.timeLog.missingWeekForEdit"));
        return;
      }
      history.push("/timesheets/time-log/edit", {
        weekStartKey: weekKey,
        timeSheetId: String(row.timeSheetId),
      });
    });
  };

  const handleRevert = async (row) => {
    const tid = row?.timeSheetId;
    if (!tid) {
      Common.showErrorMessage(t("app.timesheets.timeLog.missingTimeSheetId"));
      return;
    }
    setRevertLoading(true);
    try {
      await unsubmitTimeSheet(tid);
      Common.showSuccessMessage(t("app.timesheets.timeLog.revertSuccess"));
      setDetailRow(null);
      setDetailDto(null);
      setTimeLogRefreshKey((k) => k + 1);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.timeLog.revertFailed"));
    } finally {
      setRevertLoading(false);
    }
  };

  /** POST SubmitTimeSheet — stays on time log (no navigation to entry). */
  const handleSubmitForApprovalFromTimeLog = async (row, statusOverride) => {
    const tid = row?.timeSheetId;
    if (!tid) {
      Common.showErrorMessage(t("app.timesheets.timeLog.missingTimeSheetId"));
      return;
    }
    const st = statusOverride ?? row?.status;
    if (st === TimeSheetStatus.Rejected || st === TimeSheetStatus.Approved) return;
    setSubmitApprovalLoading(true);
    try {
      await submitTimeSheetForApproval(tid);
      Common.showSuccessMessage(
        st === TimeSheetStatus.Draft ? t("app.timesheets.entry.submitted") : t("app.timesheets.entry.resubmitSuccess")
      );
      setDetailRow(null);
      setDetailDto(null);
      setTimeLogRefreshKey((k) => k + 1);
    } catch (e) {
      handleTimesheetApiError(e, t("app.timesheets.entry.submitFailed"));
    } finally {
      setSubmitApprovalLoading(false);
    }
  };

  const logWeeklyHoursButton = (className = "") => (
    <button
      type="button"
      className={["btn btn-primary documents-btn ts-ts-primary-gradient ts-tl-header-cta", className].filter(Boolean).join(" ")}
      style={{ border: "none" }}
      onClick={goToNewEntry}
      disabled={entryNavLoading}
      aria-busy={entryNavLoading}
    >
      {entryNavLoading ? (
        <>
          <span className="spinner-border spinner-border-sm text-white me-2" role="status" aria-hidden />
          {t("app.timesheets.timeLog.ctaLogWeeklyHoursLoading")}
        </>
      ) : (
        <>
          <i className="bi bi-calendar-plus me-2" aria-hidden />
          {t("app.timesheets.timeLog.ctaLogWeeklyHours")}
        </>
      )}
    </button>
  );

  const monthlyColumns = useMemo(
    () => [
      { key: "week", label: t("app.timesheets.timeLog.colWeekRange"), sortable: false },
      { key: "projects", label: t("app.timesheets.timeLog.colProjects"), sortable: false },
      { key: "weekly", label: t("app.timesheets.timeLog.colWeeklyHours"), sortable: false, headerClassName: "text-center" },
      { key: "monthTotal", label: t("app.timesheets.timeLog.colMonthlyTotalHours"), sortable: false, headerClassName: "text-center" },
      { key: "status", label: t("app.timesheets.timeLog.colStatus"), sortable: false, headerClassName: "text-center" },
      { key: "actions", label: t("app.timesheets.timeLog.colActions"), sortable: false, headerClassName: "text-end" },
    ],
    [t]
  );

  const weeklyColumns = useMemo(
    () => [
      { key: "day", label: t("app.timesheets.timeLog.colDay"), sortable: false },
      { key: "projects", label: t("app.timesheets.timeLog.colProjectsSummary"), sortable: false },
      { key: "daily", label: t("app.timesheets.timeLog.colDailyHours"), sortable: false, headerClassName: "text-center" },
      { key: "status", label: t("app.timesheets.timeLog.colStatus"), sortable: false, headerClassName: "text-center" },
      { key: "actions", label: t("app.timesheets.timeLog.colActions"), sortable: false, headerClassName: "text-end" },
    ],
    [t]
  );

  const tableColumns = viewMode === VIEW.MONTHLY ? monthlyColumns : weeklyColumns;
  const tableRows = viewMode === VIEW.MONTHLY ? monthlyTableRows : weeklyRows;

  const renderMonthlyRow = (row) => {
    const showMissingTimesheetUi = row.isMissingWeekPlaceholder || !rowHasTimeSheetId(row);
    if (showMissingTimesheetUi) {
      const hintKey = row.isMissingWeekPlaceholder
        ? "app.timesheets.timeLog.missingWeekRowHint"
        : "app.timesheets.timeLog.missingTimesheetRowHint";
      return (
        <>
          <td className="ts-tl-cell-week">
            <div className="fw-semibold text-body">{row.weekLabel}</div>
            <div className="ts-tl-missing-week-hint small text-muted mt-1">{t(hintKey)}</div>
          </td>
          <td className="text-muted align-middle">—</td>
          <td className="text-center text-muted align-middle">—</td>
          <td className="text-center text-muted align-middle">—</td>
          <td className="text-center text-muted align-middle">—</td>
          <td className="text-end align-middle">
            <button
              type="button"
              className="btn btn-sm btn-primary documents-btn ts-ts-primary-gradient"
              onClick={() => row.weekStart && withWorkplace(() => navigateToWeeklyEntry(row.weekStart))}
              disabled={entryNavLoading || !row.weekStart}
              aria-busy={entryNavLoading}
            >
              {entryNavLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm text-white me-1" role="status" aria-hidden />
                  {t("app.timesheets.timeLog.ctaLogWeeklyHoursLoading")}
                </>
              ) : (
                <>
                  <i className="bi bi-calendar-plus me-1" aria-hidden />
                  {t("app.timesheets.timeLog.missingWeekRowCta")}
                </>
              )}
            </button>
          </td>
        </>
      );
    }

    const canEdit = canEditTimesheet(row.status);
    const showRevert = showRevertForStatus(row.status);
    const submitCta = getTimeLogSubmitCta(row.status, row.timeSheetId);
    const showSubmitArea = submitCta !== "none" && (submitCtaShowsDisabledSubmit(submitCta) || canEdit);
    return (
      <>
        <td className="fw-semibold ts-tl-cell-week">{row.weekLabel}</td>
        <td className="ts-tl-cell-project-titles">
          {row.projectTitles?.length ? (
            <ul className="ts-tl-project-list mb-0" role="list">
              {row.projectTitles.map((title, idx) => (
                <li key={`${row.id}-p-${idx}`} className="ts-tl-project-pill ts-tl-project-pill--compact" role="listitem">
                  {title}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="text-center fw-bold ts-tl-cell-duration" style={{ color: "var(--ts-primary, #005db6)" }}>
          {row.weeklyHours.toFixed(1)}h
        </td>
        <td className="text-center fw-bold text-secondary">{monthlyTotalHours.toFixed(1)}h</td>
        <td className="text-center">
          <TimeSheetStatusBadge status={row.status} label={getTimeSheetStatusLabel(row.status)} />
        </td>
        <td className="text-end">
          <div className="documents-actions-inline ts-tl-actions-row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="documents-grid-action-btn ts-tl-btn-detail" onClick={() => openMonthlyDetail(row)}>
              {t("app.timesheets.timeLog.detailView")}
            </button>
            <button
              type="button"
              className="documents-grid-action-btn documents-grid-action-btn--edit"
              disabled={!canEdit}
              title={canEdit ? undefined : t("app.timesheets.timeLog.actionEditDisabled")}
              onClick={() => goToEditEntry(row)}
            >
              {t("app.timesheets.common.edit")}
            </button>
            {showRevert ? (
              <button
                type="button"
                className="documents-grid-action-btn ts-tl-btn-revert ts-tl-btn-action-slot"
                disabled={revertLoading || submitApprovalLoading}
                title={t("app.timesheets.timeLog.revert")}
                onClick={() => handleRevert(row)}
              >
                {revertLoading ? t("app.timesheets.timeLog.reverting") : t("app.timesheets.timeLog.revert")}
              </button>
            ) : showSubmitArea ? (
              <button
                type="button"
                className="documents-grid-action-btn ts-tl-btn-submit-for-approval ts-tl-btn-action-slot"
                disabled={submitApprovalLoading || revertLoading || submitCtaShowsDisabledSubmit(submitCta)}
                title={timeLogSubmitButtonTitle(submitCta)}
                onClick={() => handleSubmitForApprovalFromTimeLog(row, row.status)}
              >
                {submitApprovalLoading ? t("app.timesheets.timeLog.submitting") : t("app.timesheets.timeLog.submit")}
              </button>
            ) : null}
          </div>
        </td>
      </>
    );
  };

  const firstWeeklyRowId = weeklyRows[0]?.id;

  const renderWeeklyRow = (row) => {
    const canEdit = canEditTimesheet(focusWeekMeta.status);
    const showRevert = showRevertForStatus(focusWeekMeta.status);
    const submitCta = getTimeLogSubmitCta(focusWeekMeta.status, focusWeekMeta.timeSheetId);
    const showSubmitArea = submitCta !== "none" && (submitCtaShowsDisabledSubmit(submitCta) || canEdit);
    const isFirstDayRow = firstWeeklyRowId != null && row.id === firstWeeklyRowId;
    return (
      <>
        <td className="fw-semibold ts-tl-cell-week">{row.dayLabel}</td>
        <td>
          <span className="ts-tl-project-pill text-wrap d-inline-block" style={{ maxWidth: 420 }}>
            {row.projectSummary}
          </span>
        </td>
        <td className="text-center fw-bold ts-tl-cell-duration" style={{ color: "var(--ts-primary, #005db6)" }}>
          {row.dailyHours.toFixed(1)}h
        </td>
        <td className="text-center">
          <TimeSheetStatusBadge status={row.status} label={getTimeSheetStatusLabel(row.status)} />
        </td>
        <td className="text-end">
          {isFirstDayRow ? (
            <div className="documents-actions-inline ts-tl-actions-row" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="documents-grid-action-btn ts-tl-btn-detail" onClick={() => openWeeklyDetail()}>
                {t("app.timesheets.timeLog.detailView")}
              </button>
              <button
                type="button"
                className="documents-grid-action-btn documents-grid-action-btn--edit"
                disabled={!canEdit}
                title={canEdit ? undefined : t("app.timesheets.timeLog.actionEditDisabled")}
                onClick={() => goToEditEntry({ ...focusWeekMeta, weekStart: focusWeekMeta.weekStart })}
              >
                {t("app.timesheets.common.edit")}
              </button>
              {showRevert ? (
                <button
                  type="button"
                  className="documents-grid-action-btn ts-tl-btn-revert ts-tl-btn-action-slot"
                  disabled={revertLoading || submitApprovalLoading}
                  title={t("app.timesheets.timeLog.revert")}
                  onClick={() => handleRevert(focusWeekMeta)}
                >
                  {revertLoading ? t("app.timesheets.timeLog.reverting") : t("app.timesheets.timeLog.revert")}
                </button>
              ) : showSubmitArea ? (
                <button
                  type="button"
                  className="documents-grid-action-btn ts-tl-btn-submit-for-approval ts-tl-btn-action-slot"
                  disabled={submitApprovalLoading || revertLoading || submitCtaShowsDisabledSubmit(submitCta)}
                  title={timeLogSubmitButtonTitle(submitCta)}
                  onClick={() => handleSubmitForApprovalFromTimeLog(focusWeekMeta, focusWeekMeta.status)}
                >
                  {submitApprovalLoading ? t("app.timesheets.timeLog.submitting") : t("app.timesheets.timeLog.submit")}
                </button>
              ) : null}
            </div>
          ) : (
            <span className="text-muted small">—</span>
          )}
        </td>
      </>
    );
  };

  const detailModalRange =
    detailDto?.weekRangeLabel ||
    (detailRow?.weekStart && detailRow?.weekEnd
      ? formatWeekRangeReadable(detailRow.weekStart, detailRow.weekEnd)
      : "");

  const detailTitle = detailDto?.weekTitle || t("app.timesheets.timeLog.detailModalTitle", { range: detailModalRange });

  const detailSubmitCta =
    detailDto != null && detailRow?.timeSheetId != null
      ? getTimeLogSubmitCta(detailDto.status, detailRow.timeSheetId)
      : "none";
  const detailShowSubmitFooter =
    detailDto != null &&
    detailSubmitCta !== "none" &&
    (submitCtaShowsDisabledSubmit(detailSubmitCta) || canEditTimesheet(detailDto.status));

  return (
    <div className="documents-page ts-page ts-page--time-log container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-clock-history" />}
        title={t("app.timesheets.timeLog.title")}
        description={t("app.timesheets.timeLog.description")}
      />

      <section
        className="ts-tl-stats-standalone"
        aria-label={t("app.timesheets.timeLog.statsAria")}
        aria-busy={timeLogLoading}
      >
        <div className="ts-tl-stats-summary" role="region">
          <div className="ts-tl-stats-summary__head">
            <div className="ts-tl-stats-summary__head-text">
              <p className="ts-tl-stats-summary__scope">{timeLogStatsScopeLabel}</p>
              <p className="ts-tl-stats-summary__title">{t("app.timesheets.timeLog.statsTitle")}</p>
            </div>
            <div className="ts-tl-stats-summary__cta">{logWeeklyHoursButton("ts-tl-stats-summary__btn")}</div>
          </div>
          <div className="ts-tl-stats-summary__counts" role="list">
            {TL_STATS_ORDER.map((key) => (
              <div
                key={key}
                className={`ts-tl-stats-chip ts-tl-stats-chip--${key}`}
                role="listitem"
                aria-label={
                  timeLogLoading
                    ? undefined
                    : `${t(`app.timesheets.timeLog.${TL_STATS_LABEL_KEY[key]}`)}: ${timeLogScopeStats.counts[key]}`
                }
              >
                <span className="ts-tl-stats-chip__accent" aria-hidden />
                <div className="ts-tl-stats-chip__main">
                  <span className="ts-tl-stats-chip__icon-wrap" aria-hidden>
                    <i className={TL_STATS_ICON[key]} />
                  </span>
                  <div className="ts-tl-stats-chip__figures">
                    <span className="ts-tl-stats-chip__value">
                      {timeLogLoading ? "—" : timeLogScopeStats.counts[key]}
                    </span>
                    <span className="ts-tl-stats-chip__label" aria-hidden>
                      {t(`app.timesheets.timeLog.${TL_STATS_LABEL_KEY[key]}`)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {timeLogScopeStats.total > 0 ? (
            <>
              <div
                className="ts-tl-stats-stack"
                role="img"
                aria-label={t("app.timesheets.timeLog.statsStackAria", {
                  unsubmitted: timeLogScopeStats.counts.unsubmitted,
                  submitted: timeLogScopeStats.counts.submitted,
                  approved: timeLogScopeStats.counts.approved,
                  returned: timeLogScopeStats.counts.returned,
                  rejected: timeLogScopeStats.counts.rejected,
                })}
              >
                <div className="ts-tl-stats-stack__track">
                  {TL_STATS_ORDER.filter((k) => timeLogScopeStats.counts[k] > 0).map((k) => (
                    <div
                      key={k}
                      className={`ts-tl-stats-stack__seg ts-tl-stats-stack__seg--${k}`}
                      style={{ flex: timeLogScopeStats.counts[k] }}
                      title={`${t(`app.timesheets.timeLog.${TL_STATS_LABEL_KEY[k]}`)}: ${timeLogScopeStats.counts[k]}`}
                    />
                  ))}
                </div>
              </div>
              <div className="ts-tl-stats-progress">
                <div className="ts-tl-stats-progress__head">
                  <span className="ts-tl-stats-progress__label" id="ts-tl-stats-progress-lbl">
                    {t("app.timesheets.timeLog.statsProgressLabel")}
                  </span>
                  <span className="ts-tl-stats-progress__pct" aria-hidden="true">
                    {timeLogScopeStats.submissionProgressPct}%
                  </span>
                </div>
                <div
                  className="ts-tl-stats-progress__track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={timeLogScopeStats.submissionProgressPct}
                  aria-labelledby="ts-tl-stats-progress-lbl"
                >
                  <div
                    className="ts-tl-stats-progress__fill"
                    style={{ width: `${timeLogScopeStats.submissionProgressPct}%` }}
                  />
                </div>
              </div>
            </>
          ) : !timeLogLoading ? (
            <p className="ts-tl-stats-summary__empty small text-muted mb-0">{t("app.timesheets.timeLog.statsEmpty")}</p>
          ) : null}
        </div>
      </section>

      <section className="ts-time-log-shell ts-time-log-page" aria-label={t("app.timesheets.timeLog.viewMode")}>
        <div
          className={[
            "ts-filters-grid ts-filters-grid--timelog ts-filters-grid--timelog-unified ts-filters-grid--timelog-toolbar-split",
            viewMode === VIEW.WEEKLY ? "ts-filters-grid--timelog-weekly" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div
            className={["ts-tl-toolbar-split", viewMode === VIEW.WEEKLY ? "ts-tl-toolbar-split--weekly" : ""]
              .filter(Boolean)
              .join(" ")}
          >
            <div
              className={["ts-tl-toolbar-split__left", viewMode === VIEW.WEEKLY ? "ts-tl-toolbar-split__left--weekly" : ""]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="ts-tl-filter-bar-label text-muted" id="ts-tl-filters-toolbar-heading">
                {t("app.timesheets.timeLog.filtersToolbarLabel")}
              </span>
              <div className="ts-tl-filter-toolbar-inner ts-tl-filter-toolbar-inner--left" aria-labelledby="ts-tl-filters-toolbar-heading">
                <div className="ts-view-tabs ts-view-tabs--two ts-view-tabs--timelog-inline" role="tablist" aria-label={t("app.timesheets.timeLog.viewMode")}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === VIEW.MONTHLY}
                    className={viewMode === VIEW.MONTHLY ? "is-active" : ""}
                    onClick={() => setViewMode(VIEW.MONTHLY)}
                  >
                    {t("app.timesheets.timeLog.viewMonthly")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === VIEW.WEEKLY}
                    className={viewMode === VIEW.WEEKLY ? "is-active" : ""}
                    onClick={() => setViewMode(VIEW.WEEKLY)}
                  >
                    {t("app.timesheets.timeLog.viewWeekly")}
                  </button>
                </div>
                <div className="ts-tl-period-toolbar-group">
                  <div
                    className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact"
                    aria-label={t("app.timesheets.timeLog.periodRangeAria")}
                  >
                    <button type="button" onClick={() => shiftMonth(-1)} aria-label={t("app.timesheets.common.prevPeriod")}>
                      <i className="bi bi-chevron-left" />
                    </button>
                    <span
                      className="ts-period-pill ts-period-pill--inline ts-period-pill--compact"
                      title={viewMode === VIEW.WEEKLY ? rangeLabel : undefined}
                    >
                      <i className="bi bi-calendar3 text-primary" aria-hidden />
                      {periodPillLabel}
                    </span>
                    <button type="button" onClick={() => shiftMonth(1)} aria-label={t("app.timesheets.common.nextPeriod")}>
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                  {showJumpToCurrentPeriod ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm ts-tl-jump-current"
                      onClick={jumpToCurrentPeriod}
                      aria-label={
                        viewMode === VIEW.MONTHLY
                          ? t("app.timesheets.timeLog.jumpToCurrentMonthAria")
                          : t("app.timesheets.timeLog.jumpToCurrentWeekToolbarAria")
                      }
                    >
                      <i className="bi bi-calendar-check me-1" aria-hidden />
                      {viewMode === VIEW.MONTHLY
                        ? t("app.timesheets.timeLog.jumpToCurrentMonth")
                        : t("app.timesheets.timeLog.jumpToCurrentWeekToolbar")}
                    </button>
                  ) : null}
                </div>
                {viewMode === VIEW.WEEKLY && weekStartsInMonth.length > 0 ? (
                  <div
                    className="ts-time-log-week-nav ts-time-log-week-nav--filter ts-time-log-week-nav--toolbar-inline"
                    role="group"
                    aria-label={t("app.timesheets.timeLog.weekNavAria")}
                  >
                    <button
                      type="button"
                      className="ts-time-log-week-nav-btn ts-time-log-week-nav-btn--toolbar"
                      onClick={() => shiftFocusWeek(-1)}
                      disabled={(() => {
                        const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
                        return i <= 0;
                      })()}
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <span className="ts-time-log-week-label" id="ts-f-week-range">
                      {focusWeekStart ? formatWeekRangeReadable(focusWeekStart, addDays(focusWeekStart, 6)) : ""}
                    </span>
                    <button
                      type="button"
                      className="ts-time-log-week-nav-btn ts-time-log-week-nav-btn--toolbar"
                      onClick={() => shiftFocusWeek(1)}
                      disabled={(() => {
                        const i = weekStartsInMonth.findIndex((w) => w.getTime() === focusWeekStart?.getTime());
                        return i < 0 || i >= weekStartsInMonth.length - 1;
                      })()}
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="ts-tl-toolbar-split__right">
              <div className="ts-tl-filter-toolbar-inner ts-tl-filter-toolbar-inner--right">
                <div className="ts-time-log-export-group ts-time-log-export-group--labeled" role="group" aria-label={t("app.timesheets.timeLog.exportActionsAria")}>
                  <button
                    type="button"
                    className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                    onClick={() => runExport("xlsx")}
                    disabled={Boolean(exportLoading) || timeLogLoading}
                    title={t("app.timesheets.timeLog.exportExcelTitle")}
                  >
                    {exportLoading === "xlsx" ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                    ) : (
                      <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                    )}
                    <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
                  </button>
                  <button
                    type="button"
                    className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                    onClick={() => runExport("csv")}
                    disabled={Boolean(exportLoading) || timeLogLoading}
                    title={t("app.timesheets.timeLog.exportCsvTitle")}
                  >
                    {exportLoading === "csv" ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                    ) : (
                      <i className="bi bi-filetype-csv" aria-hidden />
                    )}
                    <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
                  </button>
                  <button
                    type="button"
                    className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                    onClick={() => runExport("pdf")}
                    disabled={Boolean(exportLoading) || timeLogLoading}
                    title={t("app.timesheets.timeLog.exportPdfTitle")}
                  >
                    {exportLoading === "pdf" ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                    ) : (
                      <i className="bi bi-file-earmark-pdf" aria-hidden />
                    )}
                    <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
                  </button>
                </div>
                <div className="ts-filter-field ts-tl-filter-field--project">
                  <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-f-project">
                    {t("app.timesheets.timeLog.filterProject")}
                  </label>
                  <select
                    id="ts-f-project"
                    value={filters.project}
                    onChange={(e) => setFilters((f) => ({ ...f, project: e.target.value }))}
                  >
                    <option value="all">{t("app.timesheets.timeLog.allProjects")}</option>
                    {projectOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.teamName ? `${p.name} — ${p.teamName}` : p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ts-filter-field ts-tl-filter-field--status">
                  <label className="ts-tl-filter-bar-label text-muted" htmlFor="ts-f-status">
                    {t("app.timesheets.timeLog.filterStatus")}
                  </label>
                  <select
                    id="ts-f-status"
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="all">{t("app.timesheets.timeLog.anyStatus")}</option>
                    <option value={String(TimeSheetStatus.Draft)}>{getTimeSheetStatusLabel(TimeSheetStatus.Draft)}</option>
                    <option value={String(TimeSheetStatus.Submitted)}>{getTimeSheetStatusLabel(TimeSheetStatus.Submitted)}</option>
                    <option value={String(TimeSheetStatus.Approved)}>{getTimeSheetStatusLabel(TimeSheetStatus.Approved)}</option>
                  </select>
                </div>
                <div className="ts-filter-field ts-filter-field--clear-compact">
                  <span className="ts-tl-filter-bar-label ts-tl-filter-bar-label--spacer" aria-hidden="true">
                    &nbsp;
                  </span>
                  <label className="visually-hidden" htmlFor="ts-f-clear">
                    {t("app.timesheets.timeLog.clearFilters")}
                  </label>
                  <button
                    id="ts-f-clear"
                    type="button"
                    className="btn btn-outline-secondary ts-tl-clear-filters-btn"
                    onClick={() => setFilters({ project: "all", status: "all" })}
                  >
                    <i className="bi bi-x-lg me-1" aria-hidden />
                    {t("app.timesheets.timeLog.clearFilters")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <p className="documents-page-description ts-time-log-view-hint">
        {viewMode === VIEW.MONTHLY ? t("app.timesheets.timeLog.monthlyGridHint") : t("app.timesheets.timeLog.weeklyGridHint")}{" "}
        <span className="ts-tl-log-inline-hint">{t("app.timesheets.timeLog.logActionHint")}</span>
      </p>

      {viewMode === VIEW.MONTHLY && totalCount > PAGE_SIZE ? (
        <p className="small text-muted mb-2 px-1">{t("app.timesheets.timeLog.paginationTotalRows", { total: totalCount })}</p>
      ) : null}

      <TableContainer className="documents-table-wrap ts-time-log-table-wrap">
        <DataTable
          className="documents-table ts-time-log-data-table"
          columns={tableColumns}
          rows={tableRows}
          getRowKey={(row) => row.id}
          getRowClassName={(row) =>
            row.isMissingWeekPlaceholder || !rowHasTimeSheetId(row) ? "ts-tl-row-missing-week" : undefined
          }
          renderRow={(row) => (viewMode === VIEW.MONTHLY ? renderMonthlyRow(row) : renderWeeklyRow(row))}
          loading={timeLogLoading}
          loadingMessage={t("app.timesheets.timeLog.loading")}
          emptyMessage={t("app.timesheets.timeLog.empty")}
          stateCellStyle={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
        />
        {viewMode === VIEW.MONTHLY && totalPages > 1 ? (
          <div className="d-flex justify-content-center align-items-center gap-2 py-2 border-top">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page <= 1 || timeLogLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("app.timesheets.timeLog.paginationPrev")}
            </button>
            <span className="small text-muted">
              {t("app.timesheets.timeLog.paginationPage", { page, totalPages })}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages || timeLogLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("app.timesheets.timeLog.paginationNext")}
            </button>
          </div>
        ) : null}
        <div className="ts-time-log-footer-total d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 px-3 py-3 border-top">
          <div>
            <span className="fw-bold text-uppercase text-muted ts-tl-footer-label d-block">{t("app.timesheets.timeLog.totalMonthlyHours")}</span>
            {viewMode === VIEW.WEEKLY && focusWeekMeta ? (
              <span className="small text-muted">{t("app.timesheets.timeLog.footerWeekHours", { hours: focusWeekMeta.weeklyHours.toFixed(1) })}</span>
            ) : null}
          </div>
          <span className="fw-black ts-tl-footer-hours align-self-end align-self-sm-center">
            {viewMode === VIEW.MONTHLY ? monthlyTotalHours.toFixed(1) : focusWeekMeta.weeklyHours.toFixed(1)}h
          </span>
        </div>
      </TableContainer>

      {detailRow && (
        <div
          className="documents-modal-overlay ts-tl-detail-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ts-detail-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDetailRow(null);
              setDetailDto(null);
            }
          }}
        >
          <div className="documents-modal ts-tl-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-tl-detail-modal-header">
              <div className="ts-tl-detail-header-main">
                <div className="ts-tl-detail-header-icon" aria-hidden>
                  <i className="bi bi-calendar-week" />
                </div>
                <div className="ts-tl-detail-header-text">
                  <p className="ts-tl-detail-eyebrow">{t("app.timesheets.timeLog.detailModalEyebrow")}</p>
                  <h2 className="ts-tl-detail-title" id="ts-detail-title">
                    {detailLoading ? "…" : detailTitle}
                  </h2>
                  <p className="ts-tl-detail-sub">
                    {detailDto?.submittedDate
                      ? t("app.timesheets.timeLog.detailModalSubmitted", {
                          date: new Date(detailDto.submittedDate).toLocaleString(),
                        })
                      : t("app.timesheets.timeLog.detailModalSubtitle")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="ts-tl-detail-close"
                aria-label={t("app.timesheets.common.close")}
                onClick={() => {
                  setDetailRow(null);
                  setDetailDto(null);
                }}
              >
                <i className="bi bi-x-lg" aria-hidden />
                <span className="ts-tl-detail-close__label d-none d-sm-inline">{t("app.timesheets.common.close")}</span>
              </button>
            </div>
            <div className="ts-tl-detail-body">
              {detailLoading ? (
                <p className="text-center text-muted py-4">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                  {t("app.timesheets.timeLog.detailLoading")}
                </p>
              ) : detailDto?.days?.length ? (
                <table className="ts-tl-detail-table ts-tl-detail-table--stacked">
                  <thead>
                    <tr>
                      <th scope="col">{t("app.timesheets.timeLog.detailColProject")}</th>
                      <th scope="col" className="text-end">
                        {t("app.timesheets.timeLog.detailColHours")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailDto.days.map((day) => (
                      <React.Fragment key={day.dayHeading || day.dayLabel || String(day.workDate)}>
                        <tr className="ts-tl-detail-day-sep">
                          <td colSpan={2} className="ts-tl-detail-day-sep-cell">
                            {day.dayHeading || day.dayLabel}
                          </td>
                        </tr>
                        {(day.projects ?? []).map((p, idx) => (
                          <tr key={`${day.dayLabel}-${p.projectId ?? ""}-${p.projectTitle}-${idx}`}>
                            <td>{p.projectTitle}</td>
                            <td className="text-end fw-semibold">{Number(p.hours).toFixed(1)}h</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-muted py-3">{t("app.timesheets.timeLog.detailEmpty")}</p>
              )}
            </div>
            <div className="ts-tl-detail-footer ts-tl-detail-footer--actions">
              <button
                type="button"
                className="ts-tl-detail-footer-action ts-tl-detail-footer-action--primary btn btn-primary ts-ts-primary-gradient"
                disabled={!detailDto || !canEditTimesheet(detailDto.status)}
                title={detailDto && canEditTimesheet(detailDto.status) ? undefined : t("app.timesheets.timeLog.actionEditDisabled")}
                onClick={() => detailRow && goToEditEntry(detailRow)}
              >
                {t("app.timesheets.common.edit")}
              </button>
              {detailDto && showRevertForStatus(detailDto.status) ? (
                <button
                  type="button"
                  className="ts-tl-detail-footer-action ts-tl-detail-footer-action--revert btn btn-outline-secondary ts-tl-btn-revert-footer"
                  disabled={revertLoading || submitApprovalLoading}
                  title={t("app.timesheets.timeLog.revert")}
                  onClick={() => detailRow && handleRevert(detailRow)}
                >
                  {revertLoading ? t("app.timesheets.timeLog.reverting") : t("app.timesheets.timeLog.revert")}
                </button>
              ) : detailShowSubmitFooter ? (
                <button
                  type="button"
                  className="ts-tl-detail-footer-action ts-tl-detail-footer-action--submit btn ts-tl-btn-submit-footer"
                  disabled={submitApprovalLoading || revertLoading || submitCtaShowsDisabledSubmit(detailSubmitCta)}
                  title={timeLogSubmitButtonTitle(detailSubmitCta)}
                  onClick={() =>
                    detailRow && handleSubmitForApprovalFromTimeLog(detailRow, detailDto?.status ?? detailRow?.status)
                  }
                >
                  {submitApprovalLoading ? t("app.timesheets.timeLog.submitting") : t("app.timesheets.timeLog.submit")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {workplaceRequiredModal}
    </div>
  );
};

export default TimeLogPage;
